import React, { Fragment, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';

import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';

import { BiCheck } from 'react-icons/bi';
import { FaRegCalendar } from 'react-icons/fa';

import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';

import { ReservationContext } from 'context/ReservationProvider';
import { getMarinaTextId } from 'context/GlobalProvider';

import Switch from 'components/UI/Antd/Switch/Switch';
import ReservationStatus from 'components/UI/ReservationStatus/ReservationStatus';
import ImageWithFallback from 'components/UI/ImageWithFallback/ImageWithFallback';
import Button from 'components/UI/Antd/Button/Button';
import { Button as StyledButton } from 'components/common/Button';
import DatePickerDialog from './DatePickerDialog/DatePickerDialog';
import EditConfirmDialog from './EditConfirmDialog/EditConfirmDialog';

import { MARINA_PAGE } from 'settings/constant';

import { getStatus, formatPrice } from 'library/helpers/utils';
import useWindowSize from 'library/hooks/useWindowSize';
import attachmentService from './../../../services/attachment.js';

import ReservationWrapper, {
  Header,
  MarinaImageWrapper,
  MarinaTitleWrapper,
  MarinaTitle,
  MarinaInfo,
  Body,
  TitleWrapper,
  Title,
  SecondTitle,
  Divider,
  Section,
  SectionTitle,
  SectionContent,
  MainInfo,
  StatusWrapper,
  Discounts,
  AdditionalInfo,
  ConnectedCardIconWrapper,
  SuggestionsSection,
  SuggestionsTitle,
  SuggestionsDescription,
  SuggestionsList,
  Suggestion
} from './Reservation.style';

import TemCard from 'assets/images/tem-card.png';
import BFCard from 'assets/images/bf-logo.png';
import IGYCard from 'assets/images/igy_logo.svg';
import STXCard from 'assets/images/stx_logo.png';
import {formatLocalizedDate, formatLocalizedDateRange} from "../../../library/helpers/dates";
import ConfirmModal from 'components/UI/ConfirmModal/ConfirmModal';

const marinaLogos = (marina_id) => {
  return ((process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && marina_id === 82)
  || (process.env.REACT_APP_FIREBASE_ENV === 'TEST' && marina_id === 411))
}

const marinaPuertoSherry = (marina_id) => {
  return ((process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && marina_id === 98)
  || (process.env.REACT_APP_FIREBASE_ENV === 'TEST' && marina_id === 352))
  ||  (process.env.REACT_APP_FIREBASE_ENV === 'DEVELOPMENT' && marina_id ===5)
}

const ReservationDetails = ({
  marina,
  user,
  action_required,
  status_code,
  onOpenMessageDrawer,
  expired,
  calculatedPrice,
  requested_redeem_nights_amount,
  setIsZeroPrice,
  isZeroPrice,
  pickapierDiscount,
  renderPaymentActions,
  renderReservationActions,
  updateReservationDatesHandler,
  suggestions,
  source,
  cancellationPolicy
}) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const { reservation, statuses, usePopeyeBalance, setUsePopeyeBalance } =
    useContext(ReservationContext);
  const {
    vessel,
    view_id,
    start_date,
    end_date,
    marina_id,
    payment,
    popeye_discount,
    instant_book,
    payment_accepted_online,
    contract_name,
    pricing_plan  } = reservation;

  const [totalToPay, setTotalToPay] = useState(null);


  const { length: boat_length, beam, draft, type } = vessel;

  const {
    images,
    name: marina_name,
    phone,
    country_code,
    city,
    country,
    marina_settings
  } = marina;
  const { vessels } = user;

  const currentVessel = vessels.filter(({ id }) => id === vessel.id)[0];
  const activeMemberships = currentVessel.memberships.filter(
    ms => ms.customer.status === 'active'
  );

  const startDate = start_date ? moment(start_date) : null;
  const endDate = end_date ? moment(end_date) : null;
  const from = startDate.format('DD/MM/YYYY');
  const to = endDate.format('DD/MM/YYYY');
  const nights = moment(endDate).diff(moment(startDate), 'days');
  const status = getStatus(statuses[statuses.length - 1]);
  const isPaid = (action_required === 'FINISHED' && payment_accepted_online);
  const formatted_status = (status === 'APPROVED' && isPaid) ? 'APPROVED_PAID': status;
  const reservation_start_date = startDate.format('MMMM D, YYYY');
  const startDatePassed = start_date && moment(start_date).isBefore(moment(), 'days');

  const [datePickerOpened, setDatePickerOpened ] = useState(false);
  const [newStartDate, setNewStartDate ] = useState(null);
  const [newEndDate, setNewEndDate ] = useState(null);
  const [confirmEditDialogOpened, setConfirmEditDialogOpened] = useState(false);
  const [beforeConfirmOpened, setBeforeConfirmOpened] = useState(false);

  const [selectedSuggestionId, setSelectedSuggestionId ] = useState(null);
  const coverdInTheMarinaSeasons =  !isEmpty(calculatedPrice) && calculatedPrice?.count_descriptions !== "RESERVATION_IS_NOT_COVERED_IN_THE_MARINA_SEASONS";


  useEffect(() => {
    let init = null;
    const isFinished = action_required === 'FINISHED';
    if(payment) {
      init = payment.total_to_pay;
    }
    if(popeye_discount) {
      if(isFinished && payment.popeye_discount && payment.popeye_discount.total_money_saved === 0) {
        init = payment.total_to_pay;
      } else {
        init = payment.total_to_pay - popeye_discount.total_discount_for_points;
      }
    }
    setTotalToPay(init === null ? null : Math.round(init * 100) / 100);
    setUsePopeyeBalance(popeye_discount && popeye_discount.total_discount_for_points);
  }, [payment, popeye_discount, action_required, setUsePopeyeBalance]);

  useEffect(() => {
    if(datePickerOpened) {
      setNewStartDate(start_date ? moment(start_date).format('YYYY-MM-DD') : null);
      setNewEndDate(end_date ? moment(end_date).format('YYYY-MM-DD') : null);
    }
  }, [datePickerOpened, start_date, end_date]);

  const getHeadline = type => {
    // ToDo: provide all possible card titles
    const headlines = {
      TEM: t('TRANS_EUROPE_MARINAS'),
      BFG: t('BLUE_FLAG'),
      IGY: t('ISLAND_GLOBAL_YACHTING'),
      STX: t('SUNTEX_MARINAS')
    };
    return headlines[type] || t('TRANS_EUROPE_MARINAS');
  };

  const getLogo = type => {
    // ToDo: provide all possible card logos
    const logos = {
      TEM: TemCard,
      BFG: BFCard,
      IGY: IGYCard,
      STX: STXCard
    };
    return logos[type] || TemCard;
  };

  const renderPaymentSection = () => {
    // calculatedPrice is equal to reservation.pricing_plan
    if (
      (isEmpty(payment) && !coverdInTheMarinaSeasons) ||
      (isEmpty(payment) &&
        coverdInTheMarinaSeasons &&
        isNil(calculatedPrice.total_to_pay))
    ) {
      return (
        <Fragment>
          <Title className='empty-price-title'>
            <span>{t('TOTAL_PRICE')}</span>
            <span className='title-info'>
              {t('RESERVATION_REQUEST_SENT_WAITING_TOTAL_PRICE')}
            </span>
          </Title>
          <Divider className='payments-divider'/>
        </Fragment>
      );
    }

    const chargeLabels = {
      harbor_dues: t('HARBOR_DUES'),
      fees: t('FEES'),
      taxes: t('TAXES')
    };

    const totalFromCalculatedPrice = coverdInTheMarinaSeasons
      ? {
          total_to_pay: calculatedPrice.total_to_pay,
          price_per_night: calculatedPrice.price_per_night,
          subtotal_before_discount: calculatedPrice.total_before_discounts,
          marina_discount_percent: calculatedPrice.discount_percent,
          marina_discount: calculatedPrice.total_discounts,
          total_nights: calculatedPrice.reservation_duration,
          total_custom_surcharges: calculatedPrice.total_custom_surcharges
        }
      : {};

    const pickapierDiscountPrice = !isEmpty(pickapierDiscount)
      ? {
          total_to_pay: pickapierDiscount.total_to_pay,
          total_discounts: pickapierDiscount.total_discounts
        }
      : {};

    const paymentPrice = !isEmpty(payment)
      ? {
          total_to_pay: payment.total_to_pay,
          price_per_night: payment.price_per_night,
          subtotal_before_discount: payment.subtotal_before_discount,
          marina_discount_percent: payment.marina_discount_percent,
          marina_discount: payment.marina_discount,
          total_nights: payment.total_nights,
          nights_to_redeem: payment.nights_to_redeem,
          redeemed_nights_discount: payment.redeemed_nights_discount,
          additional_charges: payment.additional_charges,
          pickapier_discount: payment.pickapier_discount,
          total_custom_surcharges: payment.total_custom_surcharges,
          appliedPopeyeDiscount: isEmpty(payment.popeye_discount)
            ? null
            : payment.popeye_discount.total_money_saved
        }
      : {};

    const paymentObj = {
      ...totalFromCalculatedPrice,
      ...paymentPrice,
      ...pickapierDiscountPrice,
      ...paymentPrice.pickapier_discount
    };

    const {
      total_to_pay,
      price_per_night,
      subtotal_before_discount,
      marina_discount_percent,
      marina_discount,
      nights_to_redeem,
      redeemed_nights_discount,
      total_nights,
      additional_charges,
      total_discounts,
      total_custom_surcharges,
      appliedPopeyeDiscount
    } = paymentObj;

    const additionalCharges = !isEmpty(additional_charges)
      ? Object.keys(additional_charges).filter(
          key => additional_charges[key].amount
        )
      : [];

    const getCustomSurcharges = () => {
      if (isEmpty(payment)) {
        return calculatedPrice.count_descriptions[0].custom_surcharges.map(
          c => ({ ...c, active: true })
        );
      } else {
        return payment.custom_surcharges;
      }
    };

    const customSurcharges = Boolean(
      total_custom_surcharges && parseFloat(total_custom_surcharges)
    )
      ? getCustomSurcharges()
      : [];

    const getFormattedRequestedRedeemNightsLabel = rrna => {
      if (!rrna) return 'RESERVATION_PAGE_NO_REDEEM';
      else if (rrna === 1) return 'RESERVATION_PAGE_WITH_REDEEM_NIGHT';
      return 'RESERVATION_PAGE_WITH_REDEEM_NIGHTS';
    };

    const renderRequestedRedeemNights = () => {
      if (!isNil(requested_redeem_nights_amount)) {
        let rrna = requested_redeem_nights_amount;
        if (!isEmpty(payment)) {
          rrna = payment.nights_to_redeem || null;
        }
        if (rrna) {
          return (
            <MainInfo className='price-requested-redeem-nights'>
              {t(getFormattedRequestedRedeemNightsLabel(rrna), {
                number_of_nights: rrna,
                redeem_value: '50' // TEM 50% fixed discount percent price.
              })}
            </MainInfo>
          );
        }
      }
    };

    const discountDisplayCond =
      (marina_discount_percent && parseFloat(marina_discount_percent) > 0) ||
      (marina_discount && parseFloat(marina_discount) > 0);

    const renderPopeyeSection = () => {
      if (isEmpty(payment) || isEmpty(popeye_discount)) {
        return null;
      }

      if (appliedPopeyeDiscount === 0) {
        return null;
      }

      const {
        total_discount_for_points,
        discount_per_date,
        discount_summary,
        points_multiplier_summary,
        total_discount_amount,
        total_points_to_redeem
      } = popeye_discount;

      const discountPromotions =
        discount_per_date.filter(
          ({ promotion_type }) => promotion_type === 'DISCOUNT'
        ) || [];
      const popeyeMultiplierDefaultPromotion = points_multiplier_summary.find(
        ({ points_multiplier }) => parseFloat(points_multiplier) === 1
      );
      const pointsMultiplierPromotions =
        points_multiplier_summary.filter(
          ({ points_multiplier }) => parseFloat(points_multiplier) > 1
        ) || [];

      const discountPromotionsGrouped = discountPromotions.length
        ? groupBy(
            discountPromotions,
            ({ discount_percent }) => discount_percent
          )
        : [];

      const discount_promotions = Object.keys(discountPromotionsGrouped).map(
        key => {
          const summary = discount_summary.find(
            ({ discount_percent }) => discount_percent === key
          );
          return {
            id: key,
            discount_percent: parseFloat(key).toFixed(0),
            nights: discountPromotionsGrouped[key].length,
            discount: summary ? summary.total_price_discount.toFixed(2) : 0
          };
        }
      );

      const points_multiplier_promotions = pointsMultiplierPromotions.map(
        ({ points_multiplier, total_price_discount_for_points }, index) => {
          return {
            id: index,
            points_multiplier,
            discount: total_price_discount_for_points.toFixed(2)
          };
        }
      );

      const popeyePointsBalance = popeyeMultiplierDefaultPromotion
        ? popeyeMultiplierDefaultPromotion.total_price_discount_for_points
        : 0;

      const handleChangeUsePopeyeBalance = (checked, value) => {
        setUsePopeyeBalance(checked);
        if (checked) {
          const tot = total_to_pay - value;
          setTotalToPay(Math.round(tot * 100) / 100);
        } else {
          setTotalToPay(total_to_pay);
        }
      };

      const getPpopeyeDiscountSwitch = () => {
        // total_discount_amount - the discount from existing discount promotions - applied by default
        // total_discount_for_points - the discount from existing popeye points which can be spent by boater (switcher)
        // points_multiplier_promotions - the array of discounts which are taken from promotions (x2, x3) - multiplies existing points by switcher
        // total_discount_via_points from payment.popeye_discount - use instead total_discount_amount after APPROVING

        // if there is no discount from existing popeye points and no applied discount points
        if (
          isNil(total_discount_for_points) &&
          isEmpty(payment) &&
          isEmpty(payment.popeye_discount)
        ) {
          // no reason to display the switcher and popeye points or multiple promotions list
          return null;
        }

        if (source === "MARINA" && !isEmpty(payment.popeye_discount) && payment.popeye_discount?.total_discount_via_points){
          return null;
        }

        const isFinished = action_required === 'FINISHED';
        const popeyePointsHaveBeenApplied =
          Boolean(appliedPopeyeDiscount) &&
          parseFloat(appliedPopeyeDiscount) ===
            total_discount_amount +
              payment.popeye_discount.total_discount_via_points;

        // if there is no points in user's balance
        if (!isFinished && (isNil(total_points_to_redeem) || total_points_to_redeem === 0)) {
          // don't display the switch
          return null;
        }

        // if there reservation is finished (approved or canceled) and points haven't been applied
        if (isFinished && !popeyePointsHaveBeenApplied) {
          // no reason to display the switcher and popeye points or multiple promotions list
          return null;
        }

        const getSwitchLabel = () => {
          if (isFinished) {
            return t('POPEYE_RESERVATION_APPROVED_POINTS_LABEL');
          } else if (
            points_multiplier_promotions &&
            points_multiplier_promotions.length
          ) {
            return t('RESERVATION_PRICE_BREAKDOWN_POPEYE_TOGGLE');
          } else {
            return t('POPEYE_RESERVATION_CHECKOUT_USE_POINTS');
          }
        };

        return (
          <Fragment>
            <MainInfo className='popeye-info'>
              {getSwitchLabel()}
              {!isFinished && (
                <Switch
                  checked={usePopeyeBalance}
                  onChange={checked =>
                    handleChangeUsePopeyeBalance(
                      checked,
                      total_discount_for_points
                    )
                  }
                  size='small'
                />
              )}
            </MainInfo>

            {Boolean(status === 'APPROVED' && payment.popeye_discount.total_discount_via_points || popeyePointsBalance) && (
              <MainInfo>
                <span>{t('RESERVATION_PRICE_BREAKDOWN_POINTS')}</span>
                <span className='popeye-discount'>
                  - {formatPrice( status === 'APPROVED' ? payment.popeye_discount.total_discount_via_points : popeyePointsBalance, 'en', marina_settings.currency )}
                </span>
              </MainInfo>
            )}

            {Boolean(
              points_multiplier_promotions &&
                points_multiplier_promotions.length
            ) &&
              points_multiplier_promotions.map(p => {
                const { id, points_multiplier, discount } = p;
                return (
                  <MainInfo>
                    <span>
                      {t('RESERVATION_PRICE_BREAKDOWN_POPEYE_MULTIPLIER', {
                        multiplier_value: `${points_multiplier}x`
                      })}
                    </span>
                    <span className='popeye-discount'>
                      -{formatPrice(discount, 'en', marina_settings.currency)}
                    </span>
                  </MainInfo>
                );
              })}
          </Fragment>
        );
      };

      return (
        <Fragment>
          <Title>Popeye® Sail Club</Title>
          <Section>
            <SectionContent className='popeye-section'>
              {Boolean(discount_promotions && discount_promotions.length) &&
                discount_promotions.map(p => {
                  const { id, discount_percent, nights, discount } = p;
                  return (
                    <MainInfo>
                      <span>
                        {nights === 1
                          ? t(
                              'RESERVATION_PRICE_BREAKDOWN_POPEYE_DISCOUNT_SINGLE_NIGHT',
                              { percentage_value: `${discount_percent}%` }
                            )
                          : t('RESERVATION_PRICE_BREAKDOWN_POPEYE_DISCOUNT', {
                              percentage_value: `${discount_percent}%`,
                              number_of_nights: nights
                            })}
                      </span>
                      <span className='popeye-discount'>
                        -{formatPrice(discount, 'en', marina_settings.currency)}
                      </span>
                    </MainInfo>
                  );
                })}

              {getPpopeyeDiscountSwitch()}
            </SectionContent>
          </Section>
        </Fragment>
      );
    };

    const getTotalPrice = (withoutCurrency) => {
      let total = totalToPay === null ? total_to_pay : totalToPay;

      // the initial total_to_pay from BE if the reservation is canceled
      if (status === 'CANCELED_BY_MARINA' || status === 'CANCELED_BY_BOATER') {
        total = total_to_pay;
      }

      if (!isEmpty(payment) && !isNil(appliedPopeyeDiscount)) {

        total = total_to_pay - appliedPopeyeDiscount;
      } else {
        if (!isEmpty(popeye_discount) && popeye_discount.total_discount_amount) {
          total = total - popeye_discount.total_discount_amount;
        }
      }

      total = typeof num === 'number' ? parseFloat(total).toFixed(2) : total;
      setIsZeroPrice(!total);

      return withoutCurrency? total : formatPrice(parseFloat(total), 'en', marina_settings.currency);
    };

    let depositDescription = "";
    let depositAmount = "";
    let hasDeposit = false;
    //need to take deposit of current_pricing_plan
    if(( reservation?.payment?.current_pricing_plan?.deposit_rule)  && reservation.payment_accepted_online ) {
      switch(reservation?.payment?.current_pricing_plan?.deposit_rule?.deposit_type ) {
        case 'PERCENTAGE': {
          depositDescription = t(isPaid ? 'DEPOSIT_PAID_PERCENTAGE' : 'DEPOSIT_TO_PAY_NOW_PERCENTAGE',
              {amount: `${parseFloat(reservation?.payment?.current_pricing_plan?.deposit_rule?.amount ).toLocaleString('en', {
              maximumFractionDigits: 2
            })}%`});
          break;
        }
        case 'FIXED_AMOUNT': {
          depositDescription = t(isPaid ? 'DEPOSIT_PAID' : 'DEPOSIT_TO_PAY_NOW');
          break;
        }
        case 'NIGHTS': {
          const depositNightsCount = parseFloat( reservation?.payment?.current_pricing_plan?.deposit_rule.amount );
          const actualDuration =       total_nights ?? 1;
          const finalCount = Math.min(depositNightsCount, actualDuration);
          depositDescription = t(finalCount > 1 ?
                  (isPaid ? 'NIGHTS_DEPOSIT_PAID' : 'NIGHTS_DEPOSIT_NOW')
                  :
                  (isPaid ? 'NIGHT_DEPOSIT_PAID' : 'NIGHT_DEPOSIT_NOW'),
              {amount: `${finalCount.toLocaleString('en', { maximumFractionDigits: 0 })}`});
          break;
        }
        default:
          break;
      }
      // show if deposit available only after marina sets the price -> once payment object is defined
      hasDeposit = (reservation?.payment?.current_pricing_plan?.deposit ) &&
          (reservation.payment?.current_pricing_plan?.deposit != reservation.payment?.current_pricing_plan?.total_to_pay );
      const amount = hasDeposit ?
          ( reservation?.payment?.current_pricing_plan?.deposit ) : undefined;
      depositAmount = formatPrice(
          amount,
          user.language || 'en',
          reservation.payment?.currency || 'EUR'
      )
    }

    return (
      <Fragment>
        <Title>
          <span>{t('TOTAL_PRICE')}</span>
          {instant_book ? (
            <span>{t('INSTANT_BOOKING_RESERVATION_PRICE')}</span>
          ) : (
            <span className={expired ? 'expired' : ''}>{getTotalPrice()}</span>
          )}
        </Title>
        {reservation.payment && reservation.payment.deposit_rate && reservation.payment.deposit_rate !== 100 && <SecondTitle>
          <span>{isPaid? t('25_deposit_paid', {deposit_rate: reservation.payment.deposit_rate}) : t('25_deposit_to_pay', { deposit_rate: reservation.payment.deposit_rate })}</span>
          <span>{formatPrice( (parseFloat(payment.total_to_pay) * (reservation.payment.deposit_rate / 100).toFixed(2) ), 'en', marina_settings.currency)}</span>
        </SecondTitle>}

        { hasDeposit && <SecondTitle>
          <span>{depositDescription}</span>
          <span>{depositAmount}</span>
        </SecondTitle> }

        <Section className='payment-section'>
          <SectionContent>
            {!instant_book && (
              <Fragment>
                {status_code === 'INITIATED' && coverdInTheMarinaSeasons && (
                  <MainInfo className='price-info'>
                    {t('RESERVATION_PRICE_DISCLAIMER')}
                  </MainInfo>
                )}
                {!contract_name?  <div><MainInfo>
                  <span>{t('PRICE_PER_NIGHT')}</span>
                  <span className={expired ? 'expired' : ''}>
                    {formatPrice(
                      price_per_night,
                      'en',
                      marina_settings.currency
                    )}
                  </span>
                </MainInfo>
                <MainInfo>
                  <span>
                    <b>{`X ${total_nights} ${
                      total_nights > 1 ? t('NIGHTS') : t('NIGHT')
                    }`}</b>
                  </span>
                  <span className={expired ? 'expired' : ''}>
                    <b>
                      {formatPrice(
                        subtotal_before_discount,
                        'en',
                        marina_settings.currency
                      )}
                    </b>
                  </span>
                </MainInfo>
                </div> :
                <MainInfo>
                  <span>{t('CONTRACT_PRICE')}</span>
                  <b>
                  {formatPrice(
                    pricing_plan.total_before_discounts,
                    'en',
                    marina_settings.currency
                  )}</b>
                </MainInfo>
                }
                {renderRequestedRedeemNights()}
                <Discounts>
                  {Boolean(
                    discountDisplayCond
                  ) && (
                    <MainInfo>
                      <span>{`${t('DISCOUNT')} ${
                        marina_discount_percent &&
                        parseFloat(marina_discount_percent) > 0
                          ? '(' + marina_discount_percent + '%)'
                          : ''
                      }`}</span>
                      <span
                        className={expired ? 'expired' : ''}
                      >{`-${formatPrice(
                        marina_discount,
                        'en',
                        marina_settings.currency
                      )}`}</span>
                    </MainInfo>
                  )}

                  {!!nights_to_redeem && (
                    <MainInfo>
                      <span>{`${t(
                        'REDEEM_NIGHTS'
                      )} (${nights_to_redeem})`}</span>
                      <span
                        className={expired ? 'expired' : ''}
                      >{`-${formatPrice(
                        redeemed_nights_discount,
                        'en',
                        marina_settings.currency
                      )}`}</span>
                    </MainInfo>
                  )}
                </Discounts>
                {!!additionalCharges.length && (
                  <Discounts>
                    {additionalCharges.map(charge => {
                      const { type, amount, total } =
                        additional_charges[charge];
                      return (
                        <Fragment key={charge}>
                          <MainInfo>
                            <span>{`${chargeLabels[charge]} ${
                              type === 'PER_NIGHT' ? t('PER_NIGHT') : ''
                            }`}</span>
                            <span
                              className={expired ? 'expired' : ''}
                            >{`${formatPrice(
                              amount,
                              'en',
                              marina_settings.currency
                            )}`}</span>
                          </MainInfo>
                          {type === 'PER_NIGHT' && (
                            <MainInfo>
                              <span>
                                <b>{`X ${total_nights} ${
                                  total_nights > 1 ? t('NIGHTS') : t('NIGHT')
                                }`}</b>
                              </span>
                              <span className={expired ? 'expired' : ''}>
                                <b>
                                  {formatPrice(
                                    total,
                                    'en',
                                    marina_settings.currency
                                  )}
                                </b>
                              </span>
                            </MainInfo>
                          )}
                        </Fragment>
                      );
                    })}
                  </Discounts>
                )}

                {!!customSurcharges.length && (
                  <Discounts>
                    {customSurcharges.map(
                      ({ id, name, total_custom_surcharge, active }) => {
                        if (!active) return null;
                        return (
                          <Fragment key={id}>
                            <MainInfo>
                              <span>{name}</span>
                              <span>
                                {formatPrice(
                                  total_custom_surcharge,
                                  'en',
                                  marina_settings.currency
                                )}
                              </span>
                            </MainInfo>
                          </Fragment>
                        );
                      }
                    )}
                  </Discounts>
                )}

                {!isEmpty(payment) && (
                  <AdditionalInfo className='price-info'>
                    {t('TOTAL_PRICE_INCLUDE_ALL_ADDITIONAL_CHARGES')}
                  </AdditionalInfo>
                )}
              </Fragment>
            )}
          </SectionContent>
        </Section>

        {renderPopeyeSection()}

        <Divider className='payments-divider'/>
      </Fragment>
    );
  };

  const getEditDateDescription = () => {
    let dates = '';
    if(moment.isMoment(newStartDate) && moment.isMoment(newEndDate)) {
      dates = formatLocalizedDateRange(newStartDate, newEndDate);
    }
    return t('EDIT_RES_DESCRIPTION', { dates });
  }

  const renderSuggestionSection = () => {
    if(Boolean(!suggestions.length || status_code !== 'MARINA_ADDED_DATES')) return;

    return (
      <SuggestionsSection>
        <SuggestionsTitle>
          <FaRegCalendar className='suggestion-dates-icon' />
          {t('RESERVATION_OTHER_AVAILABILITIES_HEADER')}
        </SuggestionsTitle>
        <SuggestionsDescription>{t('RESERVATION_OTHER_AVAILABILITIES_OPTIONS')}</SuggestionsDescription>
        <SuggestionsList>
          {suggestions.map(({ id, start_date, end_date }) => {
            return (
              <Suggestion
                key={ id }
                onClick={ () => setSelectedSuggestionId(id)}
                selected={ id === selectedSuggestionId }
              >
                {`${moment(start_date, 'YYYY-MM-DD').format('DD MMM')} - ${moment(end_date, 'YYYY-MM-DD').format('DD MMM')}`}
              </Suggestion>
            )
          })}
        </SuggestionsList>
        <StyledButton
          fillWidth
          adaptive={false}
          disabled={!selectedSuggestionId}
          label={t('RESERVATION_OTHER_AVAILABILITIES_CTA')}
          onClick={() => {
            const suggestion = suggestions.find(({ id }) => id === selectedSuggestionId );
            const { start_date, end_date } = suggestion;
            updateReservationDatesHandler({ updated_start_date: start_date, updated_end_date: end_date})
          }}
        />
      </SuggestionsSection>
    )
  }

  return (
    <Fragment>
      <Body>

        <Header>
          {!!images && !!images.length && (
            <MarinaImageWrapper>
              <ImageWithFallback
                draggable={false}
                src={images[0].thumb}
                alt={`${marina_name} pic`}
              />
            </MarinaImageWrapper>
          )}
          <MarinaTitleWrapper>
            <Link
              target='_blank'
              className='link'
              to={{
                pathname: `${MARINA_PAGE}/${getMarinaTextId(marina)}`,
                search: '?via=reservation_details' // can not use state since it is a _blank.
              }}
            >
              <MarinaTitle>{marina_name}</MarinaTitle>
            </Link>
            <MarinaInfo>{`${city}, ${country}`}</MarinaInfo>
            {width > 767 && (
              <StatusWrapper>
                <ReservationStatus status={formatted_status} />
              </StatusWrapper>
            )}
            <Button
              className='message_button'
              htmlType='button'
              onClick={onOpenMessageDrawer}
              type='primary'
            >
              {t('MESSAGE_BUTTON')}
            </Button>
          </MarinaTitleWrapper>
        </Header>

        <Section className='vessel-section'>
          <SectionContent>
            {!contract_name && <MainInfo className='nights-info'>{`${nights} ${nights > 1 ? t('NIGHTS') : t('NIGHT')}`}</MainInfo>}
            {contract_name &&  <MainInfo className='contract_name-info'>{contract_name}</MainInfo> }

            <MainInfo className='dates-info'>
              <span>{formatLocalizedDateRange(startDate, endDate)}</span>
              {!expired
                && !startDatePassed
                && !(status === 'CANCELED_BY_MARINA' || status === 'CANCELED_BY_BOATER' || status === 'APPROVED' || status_code === 'MARINA_ADDED_DATES')
                && !suggestions.length
                && source !== 'MARINA'
                && (
                <StyledButton
                  medium
                  adaptive={false}
                  variant='link'
                  label={t('EDIT_RES_DATE')}
                  onClick={() => setDatePickerOpened(true)}
                />
              )}
              <DatePickerDialog
                datePickerOpened={datePickerOpened}
                closePicker={() => setDatePickerOpened(false)}
                openConfirm={() => setConfirmEditDialogOpened(true)}
                newStartDate={newStartDate}
                setNewStartDate={date => setNewStartDate(date)}
                newEndDate={newEndDate}
                setNewEndDate={date => setNewEndDate(date)}
                reservationEndDate={endDate}
              />

              <EditConfirmDialog
                visible={confirmEditDialogOpened}
                description={getEditDateDescription()}
                close={() => {
                  setConfirmEditDialogOpened(false);
                  // reset new dates
                  setNewStartDate(null);
                  setNewEndDate(null);
                }}
                confirm={() => {
                  updateReservationDatesHandler(
                    { updated_start_date: newStartDate.format('YYYY-MM-DD'), updated_end_date: newEndDate.format('YYYY-MM-DD')},
                    () => setConfirmEditDialogOpened(false)
                  )
                }}
              />
            </MainInfo>

            {Boolean(suggestions.length && status_code === 'MARINA_ADDED_DATES') ? (
              <MainInfo className='dates-error'>
                {t('RESERVATION_OTHER_AVAILABILITIES_BODY', {
                  dates: formatLocalizedDateRange(moment(from, 'DD/MM/YYYY'), moment(to, 'DD/MM/YYYY'))
                })}
              </MainInfo>
            ) : (
              <MainInfo>
                {/* {type && <span>{type}</span>} */}
                <span>
                  {boat_length && <span>{`${t('LOA_PLACEHOLDER')} ${boat_length}`}</span>}
                  {beam && <span>{`, ${t('BEAM_PLACEHOLDER')} ${beam}`}</span>}
                  {draft && <span>{`, ${t('DRAFT_PLACEHOLDER')} ${draft}`}</span>}
                </span>
              </MainInfo>
            ) }
          </SectionContent>
        </Section>

        {Boolean(!suggestions.length || status_code !== 'MARINA_ADDED_DATES') && <Divider /> }

        {width < 768 && (
          <TitleWrapper>
            <Title>{t('RESERVATION_DETAILS')}</Title>
            <StatusWrapper>
              <ReservationStatus status={formatted_status} />
            </StatusWrapper>
          </TitleWrapper>
        )}

        {renderSuggestionSection()}

        {Boolean(!suggestions.length || status_code !== 'MARINA_ADDED_DATES') && renderPaymentSection()}

        {payment_accepted_online &&
          status_code === 'UPDATED' &&
          action_required === 'BOATER' &&
          !isZeroPrice &&
          !expired ? renderPaymentActions() : renderReservationActions()
        }

        <Section>
          <SectionContent>
            <MainInfo>
              <span className='term'>
                <BiCheck />
                {t('RESERAVTION_TOTAL_PRICE_INCLUDES_VAT')}
              </span>
            </MainInfo>
            <MainInfo>
              <span className='term'>
                <BiCheck />
                {t('RESERVATION_NO_BOOKING_FEES')}
              </span>
            </MainInfo>
            <>
              {(marinaLogos(marina.id) || marinaPuertoSherry(marina.id)) &&
              <MainInfo>
              <span className='term'>
                <BiCheck />
                {t('PRICE_IS_NOT_FINAL')}
              </span>
              </MainInfo>}
              <MainInfo>
              <span className='term'>
                <BiCheck />
                  {t('CANCELATION_OPTION')}&nbsp;
                  <span className="cancellation_policy" onClick={() => {
                    if(cancellationPolicy && cancellationPolicy.url) {
                      window.open(cancellationPolicy.url);
                    }
                    else{
                      setBeforeConfirmOpened(true);
                    }
                    }}>
                  {t('CANCELLATION_POLICY')}</span>
              </span>
              </MainInfo>
              </>
              <ConfirmModal
                title={"Cancellation policy not available "}
                visible={beforeConfirmOpened}
                confirmButtonTitle={ t('OK') }
                onConfirm={() => {setBeforeConfirmOpened(false); }}
                asyncConfirm={true}
                onCancel={() => setBeforeConfirmOpened(false)}
                hideBackBtn = { true }
              >

                {t('NO_CANCELLATION_POLICY_MSG',{ marina_phone: country_code + phone} )}
              </ConfirmModal>
          </SectionContent>
        </Section>

        {!!activeMemberships && !!activeMemberships.length && (
          <Fragment>
            <Title>{t('MEMBERSHIP_CARD')}</Title>
            <Section>
              <SectionContent>
                <ConnectedCardIconWrapper>
                  <CheckCircleFilled
                    style={{ color: '#52c41a', fontSize: '16px' }}
                  />
                  <img alt={'Connect temp card'} src={getLogo(activeMemberships[0].type)} />
                </ConnectedCardIconWrapper>
                <MainInfo className='membership-card-info'>
                  {activeMemberships[0].type
                    ? getHeadline(activeMemberships[0].type)
                    : t('TRANS_EUROPE_MARINAS')}
                </MainInfo>
              </SectionContent>
            </Section>
          </Fragment>
        )}

      </Body>
    </Fragment>
  );
};

export default ReservationDetails;
